/**
 * dom.js - Utilitaires pour la manipulation du DOM
 * Fonctions helper pour créer et manipuler les éléments
 */

/**
 * Charger un fichier CSS de manière dynamique
 */
export function loadCSS(href) {
  // Vérifier si le CSS n'est pas déjà chargé
  if (document.querySelector(`link[href="${href}"]`)) {
    return;
  }

  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = href;
  document.head.appendChild(link);
}

/**
 * Créer un élément DOM avec attributs et contenu
 */
export function createElement(tag, attributes = {}, content = "") {
  const element = document.createElement(tag);

  // Ajouter les attributs
  Object.entries(attributes).forEach(([key, value]) => {
    if (key === "className") {
      element.className = value;
    } else if (key === "innerHTML") {
      element.innerHTML = value;
    } else if (key === "textContent") {
      element.textContent = value;
    } else {
      element.setAttribute(key, value);
    }
  });

  // Ajouter le contenu si fourni
  if (content) {
    element.innerHTML = content;
  }

  return element;
}

/**
 * Mettre à jour un élément existant
 */
export function updateElement(element, attributes = {}, content = null) {
  if (!element) return null;

  // Mettre à jour les attributs
  Object.entries(attributes).forEach(([key, value]) => {
    if (key === "className") {
      element.className = value;
    } else if (key === "innerHTML") {
      element.innerHTML = value;
    } else if (key === "textContent") {
      element.textContent = value;
    } else {
      element.setAttribute(key, value);
    }
  });

  // Mettre à jour le contenu si fourni
  if (content !== null) {
    element.innerHTML = content;
  }

  return element;
}

/**
 * Ajouter des événements à un élément
 */
export function addEventListeners(element, events) {
  Object.entries(events).forEach(([event, handler]) => {
    element.addEventListener(event, handler);
  });
}

/**
 * Supprimer tous les enfants d'un élément
 */
export function clearElement(element) {
  while (element.firstChild) {
    element.removeChild(element.firstChild);
  }
}

/**
 * Vérifier si un élément est visible à l'écran
 */
export function isElementVisible(element) {
  const rect = element.getBoundingClientRect();
  return (
    rect.top >= 0 &&
    rect.left >= 0 &&
    rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
    rect.right <= (window.innerWidth || document.documentElement.clientWidth)
  );
}

/**
 * Faire défiler jusqu'à un élément
 */
export function scrollToElement(element, behavior = "smooth") {
  element.scrollIntoView({
    behavior: behavior,
    block: "center",
  });
}

/**
 * Debounce une fonction
 */
export function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

/**
 * Throttle une fonction
 */
export function throttle(func, limit) {
  let inThrottle;
  return function () {
    const args = arguments;
    const context = this;
    if (!inThrottle) {
      func.apply(context, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Animer l'apparition d'un élément
 */
export function fadeIn(element, duration = 300) {
  element.style.opacity = "0";
  element.style.display = "block";
  element.style.transition = `opacity ${duration}ms ease-in-out`;

  requestAnimationFrame(() => {
    element.style.opacity = "1";
  });

  setTimeout(() => {
    element.style.transition = "";
  }, duration);
}

/**
 * Animer la disparition d'un élément
 */
export function fadeOut(element, duration = 300) {
  element.style.transition = `opacity ${duration}ms ease-in-out`;
  element.style.opacity = "0";

  setTimeout(() => {
    element.style.display = "none";
    element.style.transition = "";
  }, duration);
}

/**
 * Ajouter une classe avec animation
 */
export function addClassWithAnimation(element, className, duration = 300) {
  element.classList.add(className);

  // Forcer le reflow pour que l'animation se déclenche
  element.offsetHeight;

  setTimeout(() => {
    element.classList.add(`${className}-active`);
  }, 10);
}

/**
 * Supprimer une classe avec animation
 */
export function removeClassWithAnimation(element, className, duration = 300) {
  element.classList.remove(`${className}-active`);

  setTimeout(() => {
    element.classList.remove(className);
  }, duration);
}

/**
 * Créer un élément de notification toast
 */
export function showToast(message, type = "info", duration = 3000) {
  const toast = createElement("div", {
    className: `toast toast-${type}`,
    innerHTML: `
          <span class="toast-message">${message}</span>
          <button class="toast-close">&times;</button>
      `,
  });

  // Ajouter au container de toasts ou au body
  let toastContainer = document.querySelector(".toast-container");
  if (!toastContainer) {
    toastContainer = createElement("div", { className: "toast-container" });
    document.body.appendChild(toastContainer);
  }

  toastContainer.appendChild(toast);

  // Événement de fermeture
  toast.querySelector(".toast-close").addEventListener("click", () => {
    removeToast(toast);
  });

  // Auto-suppression
  setTimeout(() => {
    removeToast(toast);
  }, duration);

  // Animation d'entrée
  fadeIn(toast);

  return toast;
}

/**
 * Supprimer un toast
 */
function removeToast(toast) {
  fadeOut(toast, 200);
  setTimeout(() => {
    if (toast.parentNode) {
      toast.parentNode.removeChild(toast);
    }
  }, 200);
}

/**
 * Formater un nombre avec séparateurs de milliers
 */
export function formatNumber(num) {
  return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, " ");
}

/**
 * Capitaliser la première lettre
 */
export function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Générer un ID unique
 */
export function generateId(prefix = "id") {
  return `${prefix}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}
